<script setup lang="ts">
// Adapted from packages/frontend's shared AbilityCard for the studio
// preview. This is intentionally a BATTLEPLAN-ONLY SUBSET: the list-builder /
// battle-tracker affordances from the original (points, Scourge-of-Ghyran badge,
// Lumineth "Depict Rune" rendering, the once-per-battle "Used" toggle, and the
// settings/eye/tease props) were dropped because battleplan abilities never carry
// that data. Keep the header/title/body/keyword rendering in sync with the
// frontend original; do not re-add the list-builder chrome here.
//
// Import rewiring vs original:
//   - KeywordsBar → ./KeywordsBar.vue (local copy)
//   - AbilityCostBadge → ./badges/AbilityCostBadge.vue (local copy, simplified)
//   - formatText → ../utils/formatter.js (local copy)
//   - SVG assets → /src/assets/icons/ (local copies in this package)
import { computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { type IAbility } from '@scrolldex/shared';
import KeywordsBar from './KeywordsBar.vue';
import AbilityCostBadge from './badges/AbilityCostBadge.vue';
import { formatText } from '../utils/formatter.js';
import crossedSwordsIcon from '../assets/icons/swords-solid.svg';
import bowAndArrowIcon from '../assets/icons/bow-arrow-solid.svg';

const props = defineProps<{
  ability: IAbility;
  // Point cost shown as a badge (enhancements carry their cost on the upgrade,
  // not the ability — the renderer receives it here, mirroring the frontend).
  points?: number;
}>();

const nameId = computed(() => props.ability.name.toLowerCase().replace(/\s+/g, '-'));

// Map ability types to Font Awesome icon names
const iconMap: Record<string, string> = {
  offensive: crossedSwordsIcon,
  defensive: 'shield-alt',
  movement: 'running',
  special: 'star',
  damage: 'skull',
  shooting: bowAndArrowIcon,
  rallying: 'flag',
  control: 'circle',
};

function getAbilityIcon(type: string) {
  if (!type) return 'star'; // Default icon if type is not provided
  return iconMap[type.toLowerCase()] || 'star';
}
</script>
<template>
  <div :id="nameId" class="card ability-card">
    <div
      class="card-header ability-header"
      :class="'bg-' + (ability.color || 'black').toLowerCase()"
    >
      <span
        v-if="['offensive', 'shooting'].includes(ability.typeAttribute?.toLowerCase())"
        class="ability-icon"
      >
        <img
          :src="getAbilityIcon(ability.typeAttribute)"
          :alt="ability.typeAttribute"
          class="icon-img"
          :class="'icon-on-' + (ability.color || 'black').toLowerCase()"
        />
      </span>
      <span v-else class="icon" :class="'text-on-' + (ability.color || 'black').toLowerCase()">
        <font-awesome-icon :icon="getAbilityIcon(ability.typeAttribute)" />
      </span>
      <span
        class="timing"
        :class="'text-on-' + (ability.color || 'black').toLowerCase()"
        v-html="formatText(ability.timing)"
      />
      <AbilityCostBadge
        v-if="ability.cost"
        :type="ability.type"
        :cost="ability.cost"
        :name="ability.name"
      />
    </div>
    <div class="title-line">
      <div class="card-title ability-title">{{ ability.name }}</div>
    </div>
    <div class="card-body">
      <div class="ability-content">
        <div
          v-if="ability.unlockCondition"
          class="card-text ability-text"
          v-html="formatText(`*${ability.unlockCondition}*`)"
        ></div>
        <div
          v-if="ability.usedBy"
          class="card-text ability-text"
          v-html="formatText(`**Used By**: ${ability.usedBy}`)"
        />
        <div
          v-if="ability.declare"
          class="card-text ability-text"
          v-html="formatText(`**Declare**: ${ability.declare}`)"
        />
        <div class="card-text ability-text" v-html="formatText(`**Effect**: ${ability.effect}`)" />
      </div>
      <div class="ability-footer">
        <KeywordsBar :keywords="ability.keywords" />
        <span v-if="points" class="points-badge">{{ points }} pts</span>
      </div>
    </div>
  </div>
</template>

<style src="./AbilityCard.css" scoped></style>

<style scoped>
.timing {
  margin-right: auto;
}

.card.ability-card {
  position: relative;
}

.ability-header {
  display: flex;
  justify-content: center;
  align-items: center;
  /* Handle the discrepancy with the card border */
  margin-left: -2px;
  width: calc(100% + 4px);
}

.title-line {
  display: flex;
  align-items: center;
}
.ability-footer {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--st-space-2);
}
.points-badge {
  flex-shrink: 0;
  padding: 2px var(--st-space-2);
  border-radius: var(--st-radius-full);
  background: var(--badge-red);
  color: var(--badge-red-text);
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-bold);
  white-space: nowrap;
}
</style>
